import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import { LoadingState, Redux } from 'src/store'
import {
  AuditableResponse,
  GygStoreSectionsTimeSlotsType,
  ManageCateringTimeSlotsDto,
  ManageCateringTimeSlotsResponse,
  StoreOrderOffsets,
  StoreOrderOffsetsOverride,
  StoreOrderOffsetsOverrideUpdate,
  StoreSectionTimeSlot,
  StoreSectionsTimeSlotsOverrides,
  StoreSectionsTimeSlotsOverridesUpdate,
  StoreSectionsTimeSlotsUpdate
} from '@/src/types/apps/gygSections'
import {
  GYG365_RESTO_CATERING_GET_BY_STORE_ID,
  GYG365_RESTO_CATERING_MANAGE,
  GYG365_RESTO_STORE_ORDER_OFFSETS,
  GYG365_RESTO_STORE_ORDER_OFFSETS_CREATE,
  GYG365_RESTO_STORE_ORDER_OFFSETS_OVERRIDES,
  GYG365_RESTO_STORE_ORDER_OFFSETS_OVERRIDES_BY_ID,
  GYG365_RESTO_STORE_ORDER_OFFSETS_UPDATE,
  GYG365_RESTO_STORE_SECTION,
  GYG365_RESTO_STORE_SECTION_CREATE,
  GYG365_RESTO_STORE_SECTION_GET_ALL,
  GYG365_RESTO_STORE_SECTION_GET_BY_ID,
  GYG365_RESTO_STORE_SECTION_OVERRIDE_BULK_DELETE,
  GYG365_RESTO_STORE_SECTION_OVERRIDE_CREATE,
  GYG365_RESTO_STORE_SECTION_OVERRIDE_DELETE,
  GYG365_RESTO_STORE_SECTION_OVERRIDE_GET_BY_ID,
  GYG365_RESTO_STORE_SECTION_OVERRIDE_UPDATE,
  GYG365_RESTO_STORE_SECTION_UPDATE,
  GYG365_RESTO_STORE_SECTION_UPSERT_MANY
} from './action'
import { isEmpty } from 'lodash'
import { AxiosError } from 'axios'

type GygStoreSectionsTimeSlotsTypeStateType = {
  getStoreSectionTimeSlots: StoreSectionsTimeSlotsUpdate[]
  getStoreSectionTimeSlotsPending: boolean
  getStoreSectionTimeSlotsOverride: StoreSectionsTimeSlotsOverridesUpdate | null
  getStoreSectionTimeSlotsOverridePending: boolean
  getStoreSectionTimeSlot: StoreSectionsTimeSlotsUpdate[]
  getStoreOrderOffsets: StoreOrderOffsets[]
  getStoreOrderOffsetsOverride: StoreOrderOffsetsOverrideUpdate[]
  pending: boolean
  errors: any // todo
  editStatus: boolean

  // this error / success handling needs to be refactored badly
  // It's not clear to the subscriber where the error originates from
  // or which react component is responsible for handling the error
  // -- haritha
  success?: string

  /** Only show legacy success dialog in TradingHoursModal when source is 'modal'; v3 save uses MenuUpdateSuccessModal */
  lastTradingHoursSaveSource: 'v3' | 'modal' | null
  upsertManyStoreSectionData: {
    value: any
    errorMessage?: string
    loadingStatus: LoadingState
  }
  updateBulkStoreSectionData: {
    value: any
    errorMessage?: string
    loadingStatus: LoadingState
  }
  createRestoTimeslotsOverride: {
    value: any
    errorMessage?: string
    loadingStatus: LoadingState
  }

  // Catering API state
  getCateringTimeSlots: StoreSectionTimeSlot[]
  getCateringTimeSlotsPending: boolean
  manageCateringTimeSlots: {
    value: AuditableResponse<ManageCateringTimeSlotsResponse> | null
    errorMessage?: string
    loadingStatus: LoadingState
  }
}

interface GetStoreSectionsListResponse {
  data: StoreSectionsTimeSlotsUpdate[]
}

interface StoreSectionsOverridesListResponse {
  data: StoreSectionsTimeSlotsOverridesUpdate
}

interface UpdateStoreSectionsListResponse {
  data: GygStoreSectionsTimeSlotsType
}

interface UserUpdateThunkPayload {
  data: StoreSectionsTimeSlotsUpdate[]
}

interface UpdateStoreSectionOverride {
  id: string
  data: StoreSectionsTimeSlotsOverridesUpdate
}
interface UpdateStoreSectionsError {
  error: string
  errors: any[]
  statusCode: number
}
interface GetStoreOffsetsResponse {
  data: StoreOrderOffsets[]
}
interface UpdateStoreOffsetResponse {
  data: StoreOrderOffsets
}
interface GetStoreOffsetsOverrideResponse {
  data: StoreOrderOffsetsOverrideUpdate[]
}
interface GetStoreOffsetsOverrideByIdResponse {
  data: StoreOrderOffsetsOverrideUpdate
}

const initialState: GygStoreSectionsTimeSlotsTypeStateType = {
  getStoreSectionTimeSlots: [],
  getStoreSectionTimeSlot: [],
  getStoreSectionTimeSlotsOverride: null,
  getStoreSectionTimeSlotsOverridePending: false,
  getStoreOrderOffsetsOverride: [],
  errors: {},
  editStatus: false,
  pending: false,
  getStoreSectionTimeSlotsPending: false,
  getStoreOrderOffsets: [],
  lastTradingHoursSaveSource: null as 'v3' | 'modal' | null,
  upsertManyStoreSectionData: {
    loadingStatus: 'initial',
    value: []
  },
  updateBulkStoreSectionData: {
    loadingStatus: 'initial',
    value: []
  },
  createRestoTimeslotsOverride: {
    loadingStatus: 'initial',
    value: []
  },

  // Catering API state
  getCateringTimeSlots: [],
  getCateringTimeSlotsPending: false,
  manageCateringTimeSlots: {
    loadingStatus: 'initial',
    value: null
  }
}

export const createRestoTimeslots = createAsyncThunk<
  GetStoreSectionsListResponse,
  GygStoreSectionsTimeSlotsType[],
  Redux
>(GYG365_RESTO_STORE_SECTION_CREATE, async (timeslots, { extra }) => {
  try {
    const response = await extra.httpClient.post('store-section-time-slots', timeslots)
    const data = await response.data

    return data
  } catch {
    throw new Error('Failed to create Store Section TimeSlots')
  }
})

export const createRestoTimeslotsOverride = createAsyncThunk<
  StoreSectionsOverridesListResponse,
  StoreSectionsTimeSlotsOverrides[],
  Redux
>(GYG365_RESTO_STORE_SECTION_OVERRIDE_CREATE, async (timeslots, { extra, rejectWithValue }) => {
  try {
    const response = await extra.httpClient.post('store-section-time-slot-overrides', timeslots)
    const data = await response.data

    return data
  } catch (error: any) {
    if (error instanceof AxiosError) {
      if (error.response?.status === 409) {
        return rejectWithValue(error.response?.data?.message || 'Failed to update trading hours')
      }
    }

    return rejectWithValue('Failed to update trading hours')
  }
})

export const getStoreSectionsList = createAsyncThunk<GetStoreSectionsListResponse, void, Redux>(
  GYG365_RESTO_STORE_SECTION_GET_ALL,
  async (_, { extra }) => {
    const response = await extra.httpClient(`store-section-time-slots`)

    return response.data
  }
)

export const getStoreSectionData = createAsyncThunk<GetStoreSectionsListResponse, string, Redux>(
  GYG365_RESTO_STORE_SECTION_GET_BY_ID,
  async (storeId: string, { extra, rejectWithValue }) => {
    try {
      const response = await extra.httpClient(`store-section-time-slots?stores=[${storeId}]`)

      return response.data
    } catch (error: any) {
      if (error.response) {
        return rejectWithValue(error.response.data)
      }
    }
  }
)

export const getStoreSectionOverrideByID = createAsyncThunk<StoreSectionsOverridesListResponse, string, Redux>(
  GYG365_RESTO_STORE_SECTION_OVERRIDE_GET_BY_ID,
  async (storeId: string, { extra }) => {
    const response = await extra.httpClient(`store-section-time-slot-overrides/${storeId}`)

    return response.data
  }
)

export const updateStoreSectionData = createAsyncThunk<UpdateStoreSectionsListResponse, UserUpdateThunkPayload, Redux>(
  GYG365_RESTO_STORE_SECTION_UPDATE,
  async ({ data }, { extra, rejectWithValue }) => {
    try {
      const response = await extra.httpClient(`store-section-time-slots/`, { method: 'PATCH', data })

      return response.data
    } catch (error: any) {
      if (error.response) {
        return rejectWithValue(error.response.data)
      }
    }
  }
)

export const upsertManyStoreSectionData = createAsyncThunk<
  void,
  {
    storeSectionTimeSlotsToUpdate: StoreSectionsTimeSlotsUpdate[]
    storeSectionTimeSlotsToCreate: GygStoreSectionsTimeSlotsType[]
    bulkUpdate: boolean
  },
  Redux
>(
  GYG365_RESTO_STORE_SECTION_UPSERT_MANY,
  async ({ storeSectionTimeSlotsToUpdate, storeSectionTimeSlotsToCreate, bulkUpdate }, { extra, rejectWithValue }) => {
    try {
      await Promise.all([
        ...storeSectionTimeSlotsToUpdate.map(data =>
          extra.httpClient.patch(`store-section-time-slots/${data.id}`, data)
        ),
        ...(!isEmpty(storeSectionTimeSlotsToCreate)
          ? [extra.httpClient.post(`store-section-time-slots/${bulkUpdate}`, storeSectionTimeSlotsToCreate)]
          : [])
      ])
    } catch (error: any) {
      if (error instanceof AxiosError) {
        if (error.response?.status === 409) {
          return rejectWithValue(error.response?.data?.message || 'Failed to update trading hours')
        }
      }

      return rejectWithValue('Failed to update trading hours')
    }
  }
)

export const updateStoreSectionOverride = createAsyncThunk<
  StoreSectionsOverridesListResponse,
  UpdateStoreSectionOverride,
  Redux
>(GYG365_RESTO_STORE_SECTION_OVERRIDE_UPDATE, async ({ id, data }, { extra, rejectWithValue }) => {
  try {
    const response = await extra.httpClient(`store-section-time-slot-overrides/${id}`, { method: 'PATCH', data })

    return response.data
  } catch (error: any) {
    if (error.response) {
      return rejectWithValue(error.response.data)
    }
  }
})

export const updateStoreSectionOverrideBulk = createAsyncThunk<
  StoreSectionsOverridesListResponse,
  {
    data: StoreSectionsTimeSlotsOverrides[]
  },
  Redux
>(GYG365_RESTO_STORE_SECTION_OVERRIDE_UPDATE, async ({ data }, { extra, rejectWithValue }) => {
  try {
    const response = await extra.httpClient(`store-section-time-slot-overrides/bulk`, { method: 'POST', data })

    return response.data
  } catch (error: any) {
    if (error.response) {
      return rejectWithValue(error.response.data)
    }
  }
})

export const deleteStoreSectionOverride = createAsyncThunk<StoreSectionsOverridesListResponse, string, Redux>(
  GYG365_RESTO_STORE_SECTION_OVERRIDE_DELETE,
  async (id: string, { extra, rejectWithValue }) => {
    try {
      const response = await extra.httpClient(`store-section-time-slot-overrides/${id}`, { method: 'DELETE' })

      return response.data
    } catch (error: any) {
      if (error.response) {
        return rejectWithValue(error.response.data)
      }
    }
  }
)

export const deleteStoreSectionOverrideBulk = createAsyncThunk<{ data?: { deletedCount?: number } }, number[], Redux>(
  GYG365_RESTO_STORE_SECTION_OVERRIDE_BULK_DELETE,
  async (ids, { extra, rejectWithValue }) => {
    try {
      if (ids.length === 0) {
        return { data: { deletedCount: 0 } }
      }
      if (ids.length === 1) {
        const response = await extra.httpClient(`store-section-time-slot-overrides/${ids[0]}`, { method: 'DELETE' })

        return response.data
      }
      const response = await extra.httpClient.post('store-section-time-slot-overrides/bulk-delete', { ids })

      return response.data
    } catch (error: any) {
      if (error.response) {
        return rejectWithValue(error.response.data)
      }
    }
  }
)

/*
 * Order Offsets
 */

export const getStoreOrderOffsetsById = createAsyncThunk<GetStoreOffsetsResponse, string, Redux>(
  GYG365_RESTO_STORE_ORDER_OFFSETS,
  async (storeId: string, { extra, rejectWithValue }) => {
    try {
      const response = await extra.httpClient(`store-order-offsets?stores=[${storeId}]`)

      return response.data
    } catch (error: any) {
      if (error.response) {
        return rejectWithValue(error.response.data)
      }
    }
  }
)

export const updateStoreOrderOffset = createAsyncThunk<
  UpdateStoreOffsetResponse,
  { id: number; data: Partial<Pick<StoreOrderOffsets, 'value' | 'offset' | 'isActive'>> },
  Redux
>(GYG365_RESTO_STORE_ORDER_OFFSETS_UPDATE, async ({ id, data }, { extra, rejectWithValue }) => {
  try {
    const response = await extra.httpClient(`store-order-offsets/${id}`, { method: 'PATCH', data })

    return response.data
  } catch (error: any) {
    if (error.response) {
      return rejectWithValue(error.response.data)
    }
  }
})

export const createStoreOrderOffsetsOverride = createAsyncThunk<
  GetStoreOffsetsOverrideResponse,
  StoreOrderOffsetsOverride[],
  Redux
>(GYG365_RESTO_STORE_ORDER_OFFSETS_CREATE, async (offets, { extra }) => {
  try {
    const response = await extra.httpClient.post('store-order-offset-overrides', offets)
    const data = await response.data

    return data
  } catch {
    throw new Error('Failed to create Store Section TimeSlots')
  }
})

export const getStoreOrderOffsetsOverrideByStoreId = createAsyncThunk<GetStoreOffsetsOverrideResponse, string, Redux>(
  GYG365_RESTO_STORE_ORDER_OFFSETS_OVERRIDES,
  async (storeId: string, { extra, rejectWithValue }) => {
    try {
      const response = await extra.httpClient(`store-order-offset-overrides?store=[${storeId}]`)

      return response.data
    } catch (error: any) {
      if (error.response) {
        return rejectWithValue(error.response.data)
      }
    }
  }
)

export const deleteStoreOrderOffsetsOverrideById = createAsyncThunk<GetStoreOffsetsOverrideByIdResponse, string, Redux>(
  GYG365_RESTO_STORE_ORDER_OFFSETS_OVERRIDES_BY_ID,
  async (id: string, { extra, rejectWithValue }) => {
    try {
      const response = await extra.httpClient(`store-order-offset-overrides/${id}`, { method: 'DELETE' })

      return response.data
    } catch (error: any) {
      if (error.response) {
        return rejectWithValue(error.response.data)
      }
    }
  }
)

// Catering API thunks
export const getCateringTimeSlotsByStoreId = createAsyncThunk<StoreSectionTimeSlot[], number, Redux>(
  GYG365_RESTO_CATERING_GET_BY_STORE_ID,
  async (storeId: number, { extra, rejectWithValue }) => {
    try {
      const response = await extra.httpClient.get(`store-section-time-slots/catering/${storeId}`)

      return response.data.data // Fixed: return response.data.data
    } catch (error: any) {
      if (error.response) {
        return rejectWithValue(error.response.data)
      }

      return rejectWithValue('Failed to fetch catering time slots')
    }
  }
)

export const manageCateringTimeSlots = createAsyncThunk<
  AuditableResponse<ManageCateringTimeSlotsResponse>,
  ManageCateringTimeSlotsDto,
  Redux
>(GYG365_RESTO_CATERING_MANAGE, async (manageDto, { extra, rejectWithValue }) => {
  try {
    const response = await extra.httpClient.post('store-section-time-slots/catering/manage', manageDto)

    return response.data
  } catch (error: any) {
    if (error.response) {
      return rejectWithValue(error.response.data)
    }

    return rejectWithValue('Failed to manage catering time slots')
  }
})

export const gygStoreSectionTimeSlots = createSlice({
  name: GYG365_RESTO_STORE_SECTION,
  initialState,
  reducers: {
    clearUpsertManyStoreSectionData: state => {
      state.upsertManyStoreSectionData = initialState.upsertManyStoreSectionData
      state.lastTradingHoursSaveSource = null
    },
    clearUpdateBulkStoreSectionData: state => {
      state.updateBulkStoreSectionData = initialState.updateBulkStoreSectionData
      state.lastTradingHoursSaveSource = null
    },
    setTradingHoursSaveSource: (state, action: { payload: 'v3' | 'modal' }) => {
      state.lastTradingHoursSaveSource = action.payload
    },
    clearCreateRestoTimeslotsOverride: state => {
      state.createRestoTimeslotsOverride = initialState.createRestoTimeslotsOverride
      state.lastTradingHoursSaveSource = null
    },
    clearManageCateringTimeSlots: state => {
      state.manageCateringTimeSlots = initialState.manageCateringTimeSlots
    },
    clearSuccess: state => {
      state.success = undefined
    }
  },
  extraReducers: builder => {
    builder

      // Create Stores Sections Time Slot
      .addCase(createRestoTimeslots.fulfilled, (state, action) => {
        state.getStoreSectionTimeSlots = action.payload.data
        state.getStoreSectionTimeSlotsPending = false
        state.success = 'Trading hours updated successfully'
        state.errors = null
      })
      .addCase(createRestoTimeslots.pending, state => {
        state.getStoreSectionTimeSlotsPending = true
      })
      .addCase(createRestoTimeslots.rejected, (state, action) => {
        let errors

        if (action.payload !== undefined && (action.payload as UpdateStoreSectionsError).errors) {
          errors = (action.payload as UpdateStoreSectionsError).errors
        } else {
          errors = action.error
        }
        state.errors = errors
        state.success = undefined
        state.pending = false
      })

      // All Stores Sections Time Slot
      .addCase(getStoreSectionsList.fulfilled, (state, action) => {
        state.getStoreSectionTimeSlots = action.payload.data
        state.getStoreSectionTimeSlotsPending = false
        state.errors = null
      })
      .addCase(getStoreSectionsList.pending, state => {
        state.getStoreSectionTimeSlotsPending = true
      })
      .addCase(getStoreSectionsList.rejected, state => {
        state.getStoreSectionTimeSlotsPending = false
      })

      // Single Store Sections Time Slot
      .addCase(getStoreSectionData.fulfilled, (state, action) => {
        state.getStoreSectionTimeSlot = action.payload?.data
        state.pending = false
        state.success = undefined
        state.errors = null
      })
      .addCase(getStoreSectionData.pending, state => {
        state.pending = true
      })
      .addCase(getStoreSectionData.rejected, state => {
        state.pending = false
        state.success = undefined
      })

      // Update Single Store Sections Time Slot
      .addCase(updateStoreSectionData.fulfilled, (state, action) => {
        const findSection = state.getStoreSectionTimeSlot.find(
          singleTimeSlot => singleTimeSlot.id === action.payload.data.id
        )
        if (findSection) {
          findSection.openingTime = action.payload.data.openingTime
          findSection.closingTime = action.payload.data.closingTime
          findSection.isActive = action.payload.data.isActive
        }

        state.getStoreSectionTimeSlot = [...state.getStoreSectionTimeSlot]
        state.success = undefined
        state.pending = false
        state.updateBulkStoreSectionData.loadingStatus = 'success'
      })
      .addCase(updateStoreSectionData.pending, state => {
        state.pending = true
        state.updateBulkStoreSectionData.loadingStatus = 'pending'
      })
      .addCase(updateStoreSectionData.rejected, (state, action) => {
        state.errors = (action.payload as UpdateStoreSectionsError).errors
        state.pending = false
        state.success = undefined
        state.editStatus = true
        state.updateBulkStoreSectionData.loadingStatus = 'error'
        state.updateBulkStoreSectionData.errorMessage = action.payload as string
        state.updateBulkStoreSectionData.value = []
      })

      // Single Store Sections Time Slot Override
      .addCase(getStoreSectionOverrideByID.fulfilled, (state, action) => {
        state.getStoreSectionTimeSlotsOverride = action.payload.data
        state.pending = false
        state.success = undefined
        state.errors = null
      })
      .addCase(getStoreSectionOverrideByID.pending, state => {
        state.pending = true
      })
      .addCase(getStoreSectionOverrideByID.rejected, state => {
        state.pending = false
        state.success = undefined
      })

      // Update Single Store Sections Time Slot Override
      .addCase(updateStoreSectionOverride.fulfilled, (state, action) => {
        state.getStoreSectionTimeSlotsOverride = action.payload.data
        state.success = undefined
        state.pending = false
      })
      .addCase(updateStoreSectionOverride.pending, state => {
        state.pending = true
      })
      .addCase(updateStoreSectionOverride.rejected, (state, action) => {
        state.errors = (action.payload as UpdateStoreSectionsError).errors
        state.pending = false
        state.success = undefined
        state.editStatus = true
      })

      // Delete Store Sections Time Slot Override Data
      .addCase(deleteStoreSectionOverride.fulfilled, (state, action) => {
        state.getStoreSectionTimeSlotsOverride = action.payload.data
        state.success = 'Temporary Trading hours removed successfully.'
        state.pending = false
      })
      .addCase(deleteStoreSectionOverride.pending, state => {
        state.pending = true
      })
      .addCase(deleteStoreSectionOverride.rejected, (state, action) => {
        state.errors = (action.payload as UpdateStoreSectionsError).errors
        state.pending = false
        state.success = undefined
        state.editStatus = true
      })

      .addCase(deleteStoreSectionOverrideBulk.fulfilled, state => {
        state.pending = false
      })
      .addCase(deleteStoreSectionOverrideBulk.pending, state => {
        state.pending = true
      })
      .addCase(deleteStoreSectionOverrideBulk.rejected, state => {
        state.pending = false
      })

      /*
       * Store Order Offsets
       */
      .addCase(getStoreOrderOffsetsById.fulfilled, (state, action) => {
        state.getStoreOrderOffsets = action.payload.data
        state.pending = false
        state.success = undefined
        state.errors = null
      })
      .addCase(getStoreOrderOffsetsById.pending, state => {
        state.pending = true
      })
      .addCase(getStoreOrderOffsetsById.rejected, (state, action) => {
        let errors
        if (action.payload) {
          errors = action.payload
        } else {
          errors = action.error
        }
        state.errors = errors
        state.pending = false
        state.success = undefined
      })

      // Update Order Offset (Permanent)
      .addCase(updateStoreOrderOffset.fulfilled, (state, action) => {
        const updated = action.payload.data
        const idx = state.getStoreOrderOffsets.findIndex(item => item.id === updated.id)
        if (idx !== -1) {
          state.getStoreOrderOffsets[idx] = updated
          state.getStoreOrderOffsets = [...state.getStoreOrderOffsets]
        }
        state.pending = false
        state.success = 'Order Offset updated successfully'
        state.errors = null
      })
      .addCase(updateStoreOrderOffset.pending, state => {
        state.pending = true
      })
      .addCase(updateStoreOrderOffset.rejected, (state, action) => {
        let errors
        if (action.payload) {
          errors = action.payload
        } else {
          errors = action.error
        }
        state.errors = errors
        state.pending = false
        state.success = undefined
      })

      // Create Order Offset Overrides
      .addCase(createStoreOrderOffsetsOverride.fulfilled, (state, action) => {
        state.getStoreOrderOffsetsOverride = action.payload.data
        state.pending = false
        state.success = 'Order Offsets added successfully'
        state.errors = null
      })
      .addCase(createStoreOrderOffsetsOverride.pending, state => {
        state.pending = true
      })
      .addCase(createStoreOrderOffsetsOverride.rejected, (state, action) => {
        let errors

        if (action.payload !== undefined && (action.payload as UpdateStoreSectionsError).errors) {
          errors = (action.payload as UpdateStoreSectionsError).errors
        } else {
          errors = action.error
        }
        state.errors = errors
        state.success = undefined
        state.pending = false
      })

      // Get Order Offset Overrides
      .addCase(getStoreOrderOffsetsOverrideByStoreId.fulfilled, (state, action) => {
        state.getStoreOrderOffsetsOverride = action.payload.data
        state.pending = false
        state.success = undefined
        state.errors = null
      })
      .addCase(getStoreOrderOffsetsOverrideByStoreId.pending, state => {
        state.pending = true
      })
      .addCase(getStoreOrderOffsetsOverrideByStoreId.rejected, (state, action) => {
        let errors
        if (action.payload) {
          errors = action.payload
        } else {
          errors = action.error
        }
        state.errors = errors
        state.pending = false
        state.success = undefined
      })

      // Delete Order Offset Overrides
      .addCase(deleteStoreOrderOffsetsOverrideById.fulfilled, state => {
        state.pending = false
        state.success = 'Order Offset Overrides has successfully deleted'
        state.errors = null
      })
      .addCase(deleteStoreOrderOffsetsOverrideById.pending, state => {
        state.pending = true
      })
      .addCase(deleteStoreOrderOffsetsOverrideById.rejected, (state, action) => {
        let errors
        if (action.payload) {
          errors = action.payload
        } else {
          errors = action.error
        }
        state.errors = errors
        state.pending = false
        state.success = undefined
      })
      .addCase(upsertManyStoreSectionData.pending, state => {
        state.upsertManyStoreSectionData.loadingStatus = 'pending'
      })
      .addCase(upsertManyStoreSectionData.fulfilled, state => {
        state.upsertManyStoreSectionData.loadingStatus = 'success'
      })
      .addCase(upsertManyStoreSectionData.rejected, (state, action) => {
        state.upsertManyStoreSectionData.loadingStatus = 'error'
        state.upsertManyStoreSectionData.errorMessage = action.payload as string
        state.upsertManyStoreSectionData.value = []
      })
      .addCase(createRestoTimeslotsOverride.pending, state => {
        state.createRestoTimeslotsOverride.loadingStatus = 'pending'
      })
      .addCase(createRestoTimeslotsOverride.fulfilled, state => {
        state.createRestoTimeslotsOverride.loadingStatus = 'success'
      })
      .addCase(createRestoTimeslotsOverride.rejected, (state, action) => {
        state.createRestoTimeslotsOverride.loadingStatus = 'error'
        state.createRestoTimeslotsOverride.errorMessage = action.payload as string
        state.createRestoTimeslotsOverride.value = []
      })

      // Get Catering Time Slots
      .addCase(getCateringTimeSlotsByStoreId.pending, state => {
        state.getCateringTimeSlotsPending = true
      })
      .addCase(getCateringTimeSlotsByStoreId.fulfilled, (state, action) => {
        state.getCateringTimeSlots = action.payload
        state.getCateringTimeSlotsPending = false
        state.errors = null
      })
      .addCase(getCateringTimeSlotsByStoreId.rejected, (state, action) => {
        state.getCateringTimeSlots = []
        state.getCateringTimeSlotsPending = false
        state.errors = action.payload || action.error
      })

      // Manage Catering Time Slots
      .addCase(manageCateringTimeSlots.pending, state => {
        state.manageCateringTimeSlots.loadingStatus = 'pending'
      })
      .addCase(manageCateringTimeSlots.fulfilled, (state, action) => {
        state.manageCateringTimeSlots.loadingStatus = 'success'
        state.manageCateringTimeSlots.value = action.payload
        state.success = 'Catering time slots managed successfully'
        state.errors = null
      })
      .addCase(manageCateringTimeSlots.rejected, (state, action) => {
        state.manageCateringTimeSlots.loadingStatus = 'error'
        state.manageCateringTimeSlots.errorMessage = action.payload as string
        state.manageCateringTimeSlots.value = null
        state.errors = action.payload || action.error
      })
  }
})

export const {
  clearUpsertManyStoreSectionData,
  clearUpdateBulkStoreSectionData,
  clearCreateRestoTimeslotsOverride,
  clearManageCateringTimeSlots,
  clearSuccess,
  setTradingHoursSaveSource
} = gygStoreSectionTimeSlots.actions
export default gygStoreSectionTimeSlots.reducer
